/**
 * One event in zone 2 — the row, its arrow, and the submenu that folds out under it.
 *
 * ---------------------------------------------------------------------------------------------
 * TWO TARGETS ON ONE ROW, AND THAT IS THE WHOLE POINT. NAVIGATION.md: "Klikk på pilen folder
 * ut/inn. Klikk på selve navnet åpner arrangementets Overview og folder ut samtidig." So the
 * arrow is a `<button>` that navigates nowhere and the name is a `<Link>` that also expands. A
 * single control with a click-target test inside it would be neither: a link the keyboard cannot
 * expand, or a button screen readers cannot follow.
 *
 * NO `hit-target` ON THE ARROW, and that is a considered exception to DESIGN.md §9 rather than an
 * oversight. The utility grows the POINTER area to 44px with an absolutely positioned `::after`
 * centred on the button — which here would reach ~14px to the right, straight across the left
 * edge of the name, and ~7px into the rows above and below. The name would stop being clickable
 * where the eye says it starts, which breaks the requirement in the paragraph above, and the
 * arrows of the neighbouring events would overlap each other. The button is instead given the
 * full row height and 24px of width, and the name link takes the whole remaining width of the
 * sidebar — the same trade `MonthGrid` writes down for its day cells: a target with width to
 * spare does not need the overlay.
 *
 * WHICH ÅRGANG THE SUBMENU POINTS AT. The one you are in if you are inside this event, otherwise
 * this event's newest. Both halves matter: somebody working in God Nok 2026 who opens the menu
 * must not be thrown into 2027 by a link that always meant "latest", and somebody arriving from
 * `/contacts` has no year in hand, so the newest is the only defensible guess. The newest is
 * `editionsQueryOptions`' first row — that query is `year DESC` because its index is — and it is
 * asked for ONLY while the event is folded out and only when the address does not already carry
 * a year. A menu of ten events costs zero extra requests until somebody opens one.
 *
 * AN EVENT WITH NO ÅRGANG HAS NOTHING UNDER IT, AND SAYS SO. The six pages all live inside a
 * year; a festival that has none cannot address any of them, and six links to a URL that cannot
 * be built is six broken promises. It keeps its arrow — every row in the list behaves the same,
 * and a row that silently refuses to open teaches nobody anything — and folding it out says
 * "ingen årgang ennå" in the empty state's own component. `festivalDtoSchema.editionCount` is
 * already on the row, so this costs no request either: a count of zero is the answer.
 * ---------------------------------------------------------------------------------------------
 */
import type { Actor, FestivalDto } from '@tvitvi/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { editionsQueryOptions } from '@/api/queries/editions';
import { allows } from '@/auth/actor';
import { EmptyState } from '@/components/state/EmptyState';
import { ErrorState } from '@/components/state/ErrorState';
import { Skeleton } from '@/components/state/Skeleton';
import { ICONS } from '@/components/ui/icons';
import { FESTIVAL_KIND_ICON, FESTIVAL_KIND_LABEL } from '@/features/festivals/festival-kind';
import { cn } from '@/lib/cn';

import { EVENT_NAV_ITEMS, EVENT_NAV_OVERVIEW } from './event-nav';
import { NAV_ROW, NAV_ROW_BUTTON, NAV_ROW_ICON, NAV_ROW_LINK } from './nav-styles';

/** Every row in the sidebar, zone 1 and zone 2, is one of these tall. */
const ROW = NAV_ROW;

const ROW_LINK = NAV_ROW_LINK;

export interface EventNavItemProps {
  festival: FestivalDto;
  organizationId: string;
  actor: Actor | null;
  expanded: boolean;
  /** The arrow. */
  onToggle: () => void;
  /** The name — it navigates on its own, and this is the "and folds out at the same time" half. */
  onOpen: () => void;
  /** True when the address is anywhere inside this event. */
  within: boolean;
  /** The årgang in the address, when the reader is already inside this event. */
  currentEditionId: string | null;
}

export function EventNavItem({
  festival,
  organizationId,
  actor,
  expanded,
  onToggle,
  onOpen,
  within,
  currentEditionId,
}: EventNavItemProps): ReactNode {
  const { t } = useTranslation();
  const Chevron = ICONS.chevronRight;
  const KindIcon = ICONS[FESTIVAL_KIND_ICON[festival.kind]];

  return (
    <li>
      <div className={cn('flex items-stretch gap-0.5', ROW)}>
        <button
          type="button"
          aria-expanded={expanded}
          aria-label={
            expanded
              ? t('nav.event.collapse', { name: festival.name })
              : t('nav.event.expand', { name: festival.name })
          }
          data-testid={`nav-event-toggle-${festival.slug}`}
          /* Sidemenyen er navigasjon. Se buttons-look-like-buttons. */
          data-unstyled
          onClick={onToggle}
          className={cn(NAV_ROW_BUTTON, 'w-6')}
        >
          {/*
            One glyph rotated rather than two glyphs swapped: the arrow that was pointing at the
            row is the arrow now pointing down at what it opened, which is the only thing the
            movement is for (DESIGN.md §6). `prefers-reduced-motion` flattens the transition to
            0.01ms globally, and the state is still readable — the arrow has turned.
          */}
          <Chevron
            aria-hidden
            className={cn(
              'size-3.5 transition-transform duration-[var(--duration-fast)]',
              expanded && 'rotate-90',
            )}
          />
        </button>

        <Link
          to="/festivals/$festivalId"
          params={{ festivalId: festival.id }}
          /*
           * `exact`, so "active" means you are on this event's Overview and nothing else. Left
           * fuzzy — the router's default — every subpage would light the parent up as though it
           * were the page you are on, and then nothing would be able to say which of the two the
           * address actually is.
           */
          activeOptions={{ exact: true }}
          /*
           * The parent of where you are, which is not the same thing as where you are
           * (NAVIGATION.md: "markeres også det overordnede arrangementet, slik at man alltid ser
           * hvor man er"). `aria-current="location"` is the value ARIA has for exactly this — the
           * ancestor in a navigation tree — and the router's own `aria-current="page"` wins when
           * the row IS the page, because it is spread last. Visually the difference is WEIGHT and
           * not colour: a marked parent is `font-medium` on the normal ground, an active row has
           * the accent behind it (DESIGN.md §3, "farge alene bærer aldri betydning").
           */
          aria-current={within ? 'location' : undefined}
          onClick={onOpen}
          data-testid={`nav-festival-${festival.slug}`}
          className={cn(ROW_LINK, 'min-w-0 flex-1 px-2', within && 'font-semibold')}
        >
          <KindIcon aria-hidden className={NAV_ROW_ICON} />
          {/*
            The glyph is the only thing separating a festival from a venue now that the list is
            flat (NAVIGATION.md), and a glyph says nothing out loud. The word goes to the
            accessible tree, where the group headings used to carry it.
          */}
          <span className="sr-only">{t(FESTIVAL_KIND_LABEL[festival.kind])}</span>
          <span className="flex-1 truncate">{festival.name}</span>
          {/*
            `editionCount` is on the DTO so a menu of ten festivals is still one request. The eye
            gets the digit — a sidebar 232px wide has no room for "3 årganger" beside a name — and
            the accessible tree gets the sentence, because a bare number read aloud is noise. The
            same split `DepartmentList` makes for its slug column.
          */}
          {festival.editionCount > 0 ? (
            <span className="shrink-0">
              <span className="sr-only">
                {t('festivals.editionCount', { count: festival.editionCount })}
              </span>
              <span
                aria-hidden
                className="numeric font-mono text-2xs text-subtle"
                data-testid={`nav-festival-editions-${festival.slug}`}
              >
                {festival.editionCount}
              </span>
            </span>
          ) : null}
        </Link>
      </div>

      {/*
        Folded in means ABSENT, not hidden. There is no `aria-controls` on the arrow for the same
        reason: an id that points at nothing is worse than no id, and `aria-expanded` on a button
        that immediately precedes what it opens is the disclosure pattern as written.
      */}
      {expanded ? (
        <EventSubmenu
          festival={festival}
          organizationId={organizationId}
          actor={actor}
          currentEditionId={currentEditionId}
        />
      ) : null}
    </li>
  );
}

interface EventSubmenuProps {
  festival: FestivalDto;
  organizationId: string;
  actor: Actor | null;
  currentEditionId: string | null;
}

function EventSubmenu({
  festival,
  organizationId,
  actor,
  currentEditionId,
}: EventSubmenuProps): ReactNode {
  const { t } = useTranslation();

  /** The address already carries a year for this event, so nothing needs to be asked. */
  const needsLookup = currentEditionId === null && festival.editionCount > 0;
  const query = useQuery({
    ...editionsQueryOptions(organizationId, festival.id),
    enabled: needsLookup,
  });

  const items = EVENT_NAV_ITEMS.filter((item) => allows(actor, item.permission));
  const editionId = currentEditionId ?? query.data?.items[0]?.id ?? null;

  /*
   * «OVERSIKT» ØVERST (#20b), og den er den ene raden som ikke venter på en årgang.
   *
   * Den tegnes derfor UTENFOR hver av de tre tilstandene under — henting, feil og «ingen årgang
   * ennå» — fordi festivalens forside finnes uansett hvordan årgangslista lander. En festival som
   * ikke har fått sin første årgang har fortsatt en Oversikt å gå til, og den er der man lager
   * årgangen.
   */
  /*
   * ÉN OVERSIKT PER ÅRGANG (QA13 28): står man i et år, peker «Oversikt» på årets egen adresse, og
   * den er markert der — årgangssiden og festivalsiden er den samme oversikten. Uten et år i
   * adressen er det festivalens forside, som før.
   */
  const overviewLink =
    currentEditionId === null ? (
      <Link
        to={EVENT_NAV_OVERVIEW.to}
        params={{ festivalId: festival.id }}
        activeOptions={{ exact: true }}
        data-testid={`nav-event-overview-${festival.slug}`}
        className={cn(ROW_LINK, ROW, 'ps-8 pe-2')}
      >
        <span className="flex-1 truncate">{t(EVENT_NAV_OVERVIEW.labelKey)}</span>
      </Link>
    ) : (
      <Link
        to="/festivals/$festivalId/editions/$editionId"
        params={{ festivalId: festival.id, editionId: currentEditionId }}
        activeOptions={{ exact: true }}
        data-testid={`nav-event-overview-${festival.slug}`}
        className={cn(ROW_LINK, ROW, 'ps-8 pe-2')}
      >
        <span className="flex-1 truncate">{t(EVENT_NAV_OVERVIEW.labelKey)}</span>
      </Link>
    );
  const overview = allows(actor, EVENT_NAV_OVERVIEW.permission) ? <li>{overviewLink}</li> : null;

  /*
   * EIGHT PAGES YOU MAY NOT OPEN IS NOT AN EVENT WITHOUT A YEAR. The matrix is data and an
   * organisation may close every cell the submenu asks about; if it has, this renders the
   * Oversikt row alone rather than an empty frame or the "ingen årgang ennå" line, which would be
   * a claim about the festival rather than about the reader. It is the rule `Sidebar` already
   * applies to a static section with nothing left in it.
   */
  if (items.length === 0) {
    return overview === null ? null : <ul className="flex flex-col gap-px py-px">{overview}</ul>;
  }

  if (needsLookup && query.isPending) {
    return (
      <>
        <ul className="flex flex-col gap-px py-px">{overview}</ul>
        <div
          className="flex flex-col gap-px py-px ps-8 pe-2"
          data-testid={`nav-event-submenu-skeleton-${festival.slug}`}
        >
          {items.map((item) => (
            <Skeleton key={item.id} className={cn('w-full', ROW)} />
          ))}
        </div>
      </>
    );
  }

  if (needsLookup && query.isError) {
    return (
      <>
        <ul className="flex flex-col gap-px py-px">{overview}</ul>
        <ErrorState
          inline
          error={query.error}
          onRetry={() => void query.refetch()}
          className="mt-1 ms-6"
        />
      </>
    );
  }

  /*
   * No year, so no address. `editionCount` answers this without a request; the query answering
   * with an empty page after a colleague deleted the last årgang lands in the same place, which
   * is why this is one branch and not two.
   */
  if (editionId === null) {
    return (
      <>
        <ul className="flex flex-col gap-px py-px">{overview}</ul>
        <div data-testid={`nav-event-no-edition-${festival.slug}`}>
          <EmptyState compact title={t('nav.event.noEdition')} className="mt-1 ms-6" />
        </div>
      </>
    );
  }

  return (
    <ul className="flex flex-col gap-px py-px">
      {overview}
      {items.map((item) => (
        <li key={item.id}>
          <Link
            to={item.to}
            params={{ festivalId: festival.id, editionId }}
            data-testid={`nav-event-${item.id}-${festival.slug}`}
            className={cn(ROW_LINK, ROW, 'ps-8 pe-2')}
          >
            <span className="flex-1 truncate">{t(item.labelKey)}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
